import React, { useEffect } from "react";
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export default function ConfirmDeleteModal({
  open,
  t = (key, fallback) => fallback || key,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onClose,
  loading = false,
}) {
  const resolvedTitle =
    title || t("common.confirmDelete.title", "Remove section?");

  const resolvedMessage =
    message ||
    t(
      "common.confirmDelete.message",
      "Are you sure you want to remove this section? This action cannot be undone."
    );

  const resolvedConfirmText =
    confirmText || t("common.confirmDelete.confirm", "Remove");

  const resolvedCancelText =
    cancelText || t("common.confirmDelete.cancel", "Cancel");

  const resolvedLoadingText =
    t("common.confirmDelete.removing", "Removing...");

  useEffect(() => {
    if (!open) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-xl bg-red-50 p-2">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>

            <div>
              <h2 className="text-lg font-bold text-gray-900">{resolvedTitle}</h2>
              <p className="text-sm text-gray-500 mt-1">{resolvedMessage}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-5 py-4 flex items-center justify-end gap-3 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {resolvedCancelText}
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? resolvedLoadingText : resolvedConfirmText}
          </button>
        </div>
      </div>
    </div>
  );
}